import {
  Card,
  CardActionArea,
  CardActionAreaProps,
  CardProps,
  Skeleton,
  Stack,
} from '@mui/material';
import { ELEMENT } from '@schooly/constants/test-ids';
import { FC, Fragment, PropsWithChildren, ReactNode } from 'react';

export type SimpleCardProps = {
  actions?: ReactNode;
  onItemClick?: () => void;
  avatar?: ReactNode;
  selected?: boolean;
  loadingState?: {
    avatarSize: number;
    skeletons: ReactNode[];
  };
  withBorder?: boolean;
  disabled?: boolean;
  actionAreaProps?: CardActionAreaProps;
} & CardProps;

export const SimpleCard: FC<PropsWithChildren<SimpleCardProps>> = ({
  onItemClick,
  avatar,
  actions,
  children,
  loadingState,
  withBorder = false,
  disabled = false,
  actionAreaProps,
  ...props
}) => {
  return (
    <Card
      data-test-id={ELEMENT.CARD}
      {...props}
      sx={{
        borderRadius: 0,
        border: withBorder ? (theme) => `1px solid ${theme.palette.common.light2}` : 'none',
        borderBottom: (theme) => `1px solid ${theme.palette.common.light2}`,
        pointerEvents: loadingState ? 'none' : 'auto',
        ...(disabled && {
          '*': {
            color: (theme) => theme.palette.common.grey,
            pointerEvents: 'none',
          },
          svg: { opacity: 0.5 },
        }),
        ...props.sx,
      }}
    >
      <CardActionArea onClick={onItemClick} sx={{ padding: 1 }} {...actionAreaProps}>
        <Stack direction="row" justifyContent="space-between">
          <Stack
            direction="row"
            className="card-content"
            alignItems="center"
            gap={1}
            width="100%"
            minWidth={0}
          >
            {loadingState ? (
              <Skeleton
                variant="circular"
                sx={{
                  minWidth: loadingState.avatarSize,
                  maxWidth: loadingState.avatarSize,
                  minHeight: loadingState.avatarSize,
                  maxHeight: loadingState.avatarSize,
                }}
              />
            ) : (
              avatar
            )}

            {loadingState ? (
              <Stack gap={1} flex={1}>
                {loadingState.skeletons.map((skeleton, index) => (
                  <Fragment key={index}>{skeleton}</Fragment>
                ))}
              </Stack>
            ) : (
              children
            )}
          </Stack>
          <Stack minWidth={58} className="card-actions">
            {actions}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};
